import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader } from "@/components/Common";
import { Switch } from "@/components/ui/switch";

const toInput = (n) => n.toFixed(2).replace(".", ",");
const parsePrice = (v) => parseFloat(String(v).replace(",", "."));

const usePackageForm = (initial) => {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const payload = () => {
    const credits = parseInt(form.credits, 10);
    const price = parsePrice(form.price);
    if (!credits || credits < 1) return toast.error("Inserisci un numero di analisi valido (almeno 1)") && null;
    if (Number.isNaN(price)) return toast.error("Inserisci un prezzo valido, ad esempio 4,50") && null;
    return { credits, price, active: form.active };
  };
  return { form, setForm, set, payload };
};

const Fields = ({ form, set, t }) => (
  <div className="grid grid-cols-2 gap-2">
    <label className="text-[11px] text-[#5b6b60]">Analisi comprese
      <input className="field mt-1 !py-2" type="number" min="1" max="100" inputMode="numeric" value={form.credits} onChange={set("credits")} data-testid={`${t}-credits-input`} />
    </label>
    <label className="text-[11px] text-[#5b6b60]">Prezzo € (IVA incl.)
      <input className="field mt-1 !py-2" inputMode="decimal" value={form.price} onChange={set("price")} data-testid={`${t}-price-input`} />
    </label>
  </div>
);

const PackageRow = ({ p, onSaved }) => {
  const { form, set, payload } = usePackageForm({ credits: String(p.credits), price: toInput(p.price), active: p.active });
  const [busy, setBusy] = useState(false);
  const t = `admin-package-${p.id}`;
  const save = async () => {
    const body = payload();
    if (!body) return;
    setBusy(true);
    try {
      onSaved((await api.put(`/admin/packages/${p.id}`, body)).data);
      toast.success("Pacchetto aggiornato");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`space-y-3 rounded-2xl border border-[#E2DCD0] p-4 ${p.active ? "bg-white" : "bg-[#F7F4EE] opacity-80"}`} data-testid={t}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-serif text-2xl font-semibold text-[#0D2517]" data-testid={`${t}-title`}>{p.credits} analisi · {toInput(p.price)} €</p>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${p.active ? "bg-[#059669] text-white" : "bg-[#8a958d] text-white"}`} data-testid={`${t}-status`}>{p.active ? "Attivo" : "Disattivato"}</span>
      </div>
      <p className="text-[11px] text-[#8a958d]">Acquisti completati: {p.purchases}</p>
      <Fields form={form} set={set} t={t} />
      <label className="flex items-center justify-between rounded-xl bg-[#F7F4EE] px-3 py-2.5 text-sm">
        <span>Visibile agli utenti</span>
        <Switch checked={form.active} onCheckedChange={set("active")} data-testid={`${t}-active-switch`} />
      </label>
      <button disabled={busy} onClick={save} className="btn-primary w-full !py-2 text-xs" data-testid={`${t}-save`}>{busy ? "Attendere…" : "SALVA MODIFICHE"}</button>
    </div>
  );
};

const NewPackage = ({ onSaved }) => {
  const { form, setForm, set, payload } = usePackageForm({ credits: "", price: "", active: true });
  const [busy, setBusy] = useState(false);
  const create = async () => {
    const body = payload();
    if (!body) return;
    setBusy(true);
    try {
      onSaved((await api.post("/admin/packages", body)).data);
      setForm({ credits: "", price: "", active: true });
      toast.success("Nuovo pacchetto creato");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-dashed border-[#c9a96e] bg-white p-4" data-testid="admin-package-new">
      <p className="flex items-center gap-2 text-sm font-semibold text-[#0D2517]"><Plus size={16} className="text-[#B39257]" /> Nuovo pacchetto</p>
      <Fields form={form} set={set} t="admin-package-new" />
      <button disabled={busy} onClick={create} className="btn-primary w-full !py-2 text-xs" data-testid="admin-package-new-create">{busy ? "Attendere…" : "CREA PACCHETTO"}</button>
    </div>
  );
};

export default function ProPackages() {
  const [packages, setPackages] = useState(null);
  useEffect(() => { api.get("/admin/packages").then(({ data }) => setPackages(data)); }, []);
  if (!packages) return <Loader />;
  return (
    <div className="space-y-5 px-5 py-6" data-testid="pro-packages-page">
      <div>
        <p className="eyebrow">Area riservata</p>
        <h1 className="font-serif text-4xl font-semibold text-[#0D2517]">Pacchetti analisi</h1>
        <p className="mt-1 text-sm text-[#5b6b60]">Prezzi in euro, IVA inclusa. Le modifiche valgono solo per i nuovi acquisti. Un pacchetto disattivato non è più acquistabile ma il suo storico resta conservato.</p>
      </div>
      {packages.map((p) => <PackageRow key={`${p.id}-${p.credits}-${p.price}-${p.active}`} p={p} onSaved={setPackages} />)}
      <NewPackage onSaved={setPackages} />
    </div>
  );
}
